import {
  useLayoutEffect,
  useRef,
} from "react";
import { Button as AriaButton } from "react-aria-components";

import abnormalIcon from "@/assets/figma/chat-activity/abnormal.svg";
import deckBack from "@/assets/figma/chat-activity/deck-back.svg";
import deckMiddle from "@/assets/figma/chat-activity/deck-middle.svg";
import { cn } from "@/shared/lib/cn";
import {
  MerryCardMenu,
  type MerryCardMenuAction,
  type MerryCardMenuCatalog,
} from "@/shared/ui/MerryCardMenu";
import { MerryChatThreadSummary } from "@/shared/ui/MerryChatThreadSummary";
import {
  getMerryChatHistoryDrawerSize,
  getMerryChatNotificationFigmaNodeId,
  type MerryChatActivityItem,
} from "@/shared/ui/merryChatActivityModel";

function NotificationGlassLayers() {
  return (
    <>
      <span aria-hidden="true" className="merry-chat-notification__glass" />
      <span aria-hidden="true" className="merry-chat-notification__stroke" />
    </>
  );
}

export interface MerryChatNotificationStackProps {
  className?: string;
  items: ReadonlyArray<MerryChatActivityItem>;
  onOpenNotification?: () => void;
}

export function MerryChatNotificationStack({
  className,
  items,
  onOpenNotification,
}: MerryChatNotificationStackProps) {
  if (items.length === 0) return null;

  // 进行中状态只保留左侧导航提示。
  if (items.length === 1 && items[0]?.status === "progress") return null;

  const hasMultipleItems = items.length > 1;
  const figmaNodeId = getMerryChatNotificationFigmaNodeId({
    isExpanded: false,
    isManagementVisible: false,
    items,
  });

  return (
    <section
      aria-label="聊天任务通知"
      className={cn("merry-chat-notification", className)}
      data-chat-notification-mode={hasMultipleItems ? "stacked" : "single"}
      data-figma-node-id={figmaNodeId ?? undefined}
    >
      <div className="merry-chat-notification__list">
        {items.map((item, index) => (
          <AriaButton
            aria-label={`查看通知：${item.title}`}
            className={({ isFocusVisible, isHovered, isPressed }) =>
              cn(
                "merry-chat-notification__card",
                isHovered && "merry-chat-notification__card--hovered",
                isPressed && "merry-chat-notification__card--pressed",
                isFocusVisible && "merry-chat-notification__card--focus-visible",
              )
            }
            data-chat-notification-card-id={item.id}
            key={item.id}
            onPress={onOpenNotification}
            style={{ zIndex: items.length - index + 2 }}
            type="button"
          >
            <NotificationGlassLayers />
            <MerryChatThreadSummary
              className="merry-chat-notification__summary"
              iconSource={abnormalIcon}
              item={item}
            />
          </AriaButton>
        ))}
      </div>

      {hasMultipleItems ? (
        <>
          <span aria-hidden="true" className="merry-chat-notification__deck merry-chat-notification__deck--middle">
            <img alt="" src={deckMiddle} />
          </span>
          <span aria-hidden="true" className="merry-chat-notification__deck merry-chat-notification__deck--back">
            <img alt="" src={deckBack} />
          </span>
        </>
      ) : null}
    </section>
  );
}

export interface MerryChatHistoryDrawerProps {
  catalog: MerryCardMenuCatalog;
  className?: string;
  largeContentThreshold?: number;
  onAction?: (action: MerryCardMenuAction) => void;
}

export function MerryChatHistoryDrawer({
  catalog,
  className,
  largeContentThreshold = 7,
  onAction,
}: MerryChatHistoryDrawerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const itemCount = catalog.chatHistory.sections.reduce(
    (count, section) => count + section.items.length,
    0,
  );
  const drawerSize = getMerryChatHistoryDrawerSize(
    itemCount,
    largeContentThreshold,
  );
  const isLargeContent = drawerSize === "viewport";

  useLayoutEffect(() => {
    if (isLargeContent) return;

    const root = rootRef.current;
    if (!root) return;

    const updateAvailableHeight = () => {
      const top = root.getBoundingClientRect().top;
      const availableHeight = Math.max(0, window.innerHeight - top - 24);
      root.style.setProperty(
        "--merry-chat-history-height",
        `${Math.min(656, availableHeight)}px`,
      );
    };

    updateAvailableHeight();
    window.addEventListener("resize", updateAvailableHeight);
    return () => window.removeEventListener("resize", updateAvailableHeight);
  }, [isLargeContent]);

  return (
    <div
      aria-label="聊天历史列表"
      className={cn("merry-chat-history-drawer", className)}
      data-chat-history-size={drawerSize}
      data-figma-node-id={isLargeContent ? "567:123787" : "567:122750"}
      ref={rootRef}
      role="region"
    >
      <MerryCardMenu
        animateEntrance={false}
        aria-label="聊天历史"
        catalog={catalog}
        className="merry-chat-history-drawer__menu"
        data-testid="merry-chat-history-menu"
        onAction={(action) => onAction?.(action)}
        view="chat-history"
      />
    </div>
  );
}
